import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonBase, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import { changeLanguage, language, tr } from 'src/locales/tr';
import { BRAND, RADIUS } from 'src/theme/ctmass-tokens';

const OPTIONS = [
  { value: 'en', short: 'EN', label: 'English' },
  { value: 'es', short: 'ES', label: 'Español' }
];

const FullSwitcher = () => (
  <Stack
    direction="row"
    spacing={1}
  >
    {OPTIONS.map((option) => {
      const active = option.value === language;
      return (
        <ButtonBase
          aria-pressed={active}
          key={option.value}
          onClick={() => changeLanguage(option.value)}
          sx={{
            flex: 1,
            minHeight: 44,
            borderRadius: RADIUS.tile,
            fontSize: 14,
            fontWeight: 600,
            color: active ? BRAND.navy : '#FFFFFF',
            bgcolor: active ? '#FFFFFF' : alpha('#FFFFFF', 0.1),
            boxShadow: `inset 0 0 0 1px ${alpha('#FFFFFF', active ? 1 : 0.18)}`,
            transition: 'background-color .2s ease',
            '&:hover': { bgcolor: active ? '#FFFFFF' : alpha('#FFFFFF', 0.18) },
            '&:focus-visible': { outline: `2px solid ${BRAND.green}`, outlineOffset: 2 }
          }}
        >
          {option.label}
        </ButtonBase>
      );
    })}
  </Stack>
);

export const LanguageSwitcher = ({ variant = 'compact' }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  if (variant === 'full') {
    return <FullSwitcher />;
  }

  const current = OPTIONS.find((option) => option.value === language) || OPTIONS[0];

  return (
    <>
      <Tooltip title={tr('Language')}>
        <ButtonBase
          aria-haspopup="menu"
          aria-label={tr('Language')}
          onClick={() => setOpen(true)}
          ref={anchorRef}
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            flexShrink: 0,
            gap: 0.5,
            height: 40,
            px: 1,
            borderRadius: RADIUS.tile,
            fontSize: 13,
            fontWeight: 700,
            color: BRAND.navy,
            transition: 'background-color .2s ease',
            '&:hover': { bgcolor: alpha(BRAND.navy, 0.08) },
            '& svg': { fontSize: 18 }
          }}
        >
          <LanguageRoundedIcon />
          {current.short}
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={() => setOpen(false)}
        open={open}
      >
        {OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setOpen(false);
              changeLanguage(option.value);
            }}
            selected={option.value === language}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

LanguageSwitcher.propTypes = {
  variant: PropTypes.oneOf(['compact', 'full'])
};
